import React, { useState, useRef, useEffect } from 'react';
import { CloseIcon, ImageIcon, UploadIcon, TrashIcon } from './Icons';
import { API_URL } from '../config';
import '../styles/SliderModal.css';

const SliderModal = ({ isOpen, onClose, onInsert, editingData = null }) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // 支持的图片格式
  const acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const acceptString = acceptedImageTypes.join(',');

  // 重置表单
  const resetForm = () => {
    if (editingData && editingData.files) {
      // 编辑模式：预填充数据
      setFiles(editingData.files.map(file => ({
        id: file.id,
        name: file.name,
        url: file.url,
        size: file.size || 0,
        uploaded: true
      })));
    } else {
      // 新建模式：清空表单
      setFiles([]);
    }
    setError('');
    setIsUploading(false);
    setUploadProgress({});
  };

  // 当弹窗打开时重置表单
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, editingData]);

  // 验证文件类型
  const validateFile = (file) => {
    if (!acceptedImageTypes.includes(file.type)) {
      return '只支持 JPG、PNG、GIF、WebP 格式的图片';
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB
      return '图片大小不能超过 10MB';
    }
    return null;
  };

  // 处理文件选择
  const handleFileSelect = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    const validFiles = [];
    const errors = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          preview: URL.createObjectURL(file),
          uploaded: false
        });
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError('');
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  // 处理拖拽
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  // 移除文件
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    // 清理预览URL
    const file = files.find(f => f.id === fileId);
    if (file && file.preview && !file.uploaded) {
      URL.revokeObjectURL(file.preview);
    }
  };

  // 上传文件
  const uploadFiles = async () => {
    const filesToUpload = files.filter(f => !f.uploaded);
    if (filesToUpload.length === 0) return files;

    setIsUploading(true);
    const uploadedFiles = [];
    const existingFiles = files.filter(f => f.uploaded);

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const fileItem = filesToUpload[i];
        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 0 }));

        const formData = new FormData();
        formData.append('files', fileItem.file);

        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`上传 ${fileItem.name} 失败`);
        }

        const result = await response.json();
        const uploadedFile = result[0];

        uploadedFiles.push({
          id: uploadedFile.id,
          name: uploadedFile.name,
          url: uploadedFile.url,
          size: uploadedFile.size
        });

        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 100 }));
        
        // 清理预览URL
        if (fileItem.preview) {
          URL.revokeObjectURL(fileItem.preview);
        }
      }

      return [...existingFiles, ...uploadedFiles];
    } catch (error) {
      console.error('上传失败:', error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  // 处理插入轮播
  const handleInsert = async () => {
    if (files.length === 0) {
      setError('请至少选择一张图片');
      return;
    }

    try {
      const finalFiles = await uploadFiles();
      
      const sliderData = {
        __component: 'shared.slider',
        files: finalFiles,
        id: editingData?.id || Date.now().toString()
      };

      onInsert(sliderData);
      onClose();
    } catch (error) {
      setError('上传失败，请重试');
    }
  };

  // 处理键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="slider-modal-overlay" onClick={onClose}>
      <div className="slider-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="modal-header">
          <div className="modal-title">
            <ImageIcon size={20} />
            <h3>{editingData ? '编辑图片轮播' : '添加图片轮播'}</h3>
          </div>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* 文件上传区域 */}
          <div 
            ref={dropZoneRef}
            className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="upload-content">
              <UploadIcon size={48} />
              <h4>拖拽图片到这里或点击选择</h4>
              <p>支持 JPG、PNG、GIF、WebP 格式，单个文件最大 10MB</p>
              <p>可以同时选择多张图片</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptString}
              onChange={(e) => handleFileSelect(e.target.files)}
              multiple
              style={{ display: 'none' }}
            />
          </div>

          {/* 已选择的文件列表 */}
          {files.length > 0 && (
            <div className="selected-files">
              <h4>已选择的图片 ({files.length})</h4>
              <div className="files-grid">
                {files.map((fileItem) => (
                  <div key={fileItem.id} className="file-item">
                    <div className="file-preview">
                      <img 
                        src={fileItem.uploaded ? 
                          (fileItem.url.startsWith('http') ? fileItem.url : `${API_URL}${fileItem.url}`) : 
                          fileItem.preview
                        } 
                        alt={fileItem.name}
                      />
                      <button 
                        className="remove-file-btn"
                        onClick={() => removeFile(fileItem.id)}
                        title="移除图片"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                    <div className="file-info">
                      <p className="file-name">{fileItem.name}</p>
                      <p className="file-size">{(fileItem.size / 1024 / 1024).toFixed(2)} MB</p>
                      {uploadProgress[fileItem.id] !== undefined && (
                        <div className="upload-progress">
                          <div 
                            className="progress-bar" 
                            style={{ width: `${uploadProgress[fileItem.id]}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            取消
          </button>
          <button 
            className="btn-insert" 
            onClick={handleInsert}
            disabled={files.length === 0 || isUploading}
          >
            {isUploading ? '上传中...' : (editingData ? '更新轮播' : '插入轮播')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SliderModal;